import './DocumentsTree.less';
import { Scrollbar } from '@adtech/ui';
import Search from '@components/Search';
import { useLazyGetDocumentChildrenQuery } from '@redux/api/adminApi';
import { Loader } from '@sberlab/core-components';
import { IconShevronDown16 } from '@sberlab/core-icons';
import { IDocumentDTO } from '@typings/rootSlice';
import cn from 'classnames';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

interface IProps {
    items: Record<string, IDocumentDTO>;
    hideSearch?: boolean;
    onClickItem?: (isClickOnIcon: boolean) => void;
}

const DocumentsTree: React.FC<IProps> = ({ items: itemsProp, hideSearch = false, onClickItem = () => {} }) => {
    const params = useParams();
    const namespace = params.namespace || 'help';
    let docName = params.document;
    const menuItems = itemsProp || {};
    const menuItemsOnPrevRender = useRef(menuItems);

    const [getDocChildren, { isFetching: isDocChildrenLoading }] = useLazyGetDocumentChildrenQuery();

    // Собираем корневые документы и сортируем их по позициям
    const rootIds = Object.keys(menuItems)
        .filter((item) => !menuItems[item]?.parent_document_id && menuItems[item]?.is_published)
        .sort((a, b) => menuItems[a].position - menuItems[b].position);

    if (!docName) docName = rootIds?.[0]; // для корня берем адрес первой страницы

    const getExpandedNodes = () => {
        const nodes = {};
        const child = menuItems[docName]?.children?.length && menuItems[docName]?.children[0];

        // Раскрываем первый узел, только если есть данные по его потомкам
        if (menuItems[child]) {
            Object.keys(menuItems).forEach((key) => {
                nodes[key] = key === docName;
            });
        }

        // Все родители активного элемента помечаем раскрытыми
        const setExpandedAllParents = (item: IDocumentDTO) => {
            const parentId = item?.parent_document_id;
            const parentName = Object.keys(menuItems).find((el) => menuItems[el]?.document_id === parentId);

            if (!parentId) return;

            nodes[parentName] = true;
            setExpandedAllParents(menuItems[parentName]);
        };

        setExpandedAllParents(menuItems[docName]);

        return nodes;
    };
    const [expandedNodes, setExpandedNodes] = useState(getExpandedNodes());

    // При первом рендере после запроса и получения реальных menuItems заново помечаем раскрытые узлы
    useEffect(() => {
        const isItems = Object.keys(menuItems)?.length;
        const isPrevItems = Object.keys(menuItemsOnPrevRender.current)?.length;
        if (isItems && !isPrevItems) {
            setExpandedNodes(getExpandedNodes());
        }
        menuItemsOnPrevRender.current = menuItems;
    }, [menuItems]);

    function Menu({ itemIds, itemsById, isRootList = false }) {
        const listClassName = cn('docs-tree__list', {
            first: isRootList,
        });

        return (
            <div className={listClassName}>
                {itemIds.map((id) => (
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    <MenuItem key={id} itemId={id} itemsById={itemsById} isRootList={isRootList} />
                ))}
            </div>
        );
    }

    function MenuItem({ itemId, itemsById, isRootList }) {
        const item = itemsById[itemId] as IDocumentDTO;

        if (!item) return null;

        // Убрать фильтр по is_published после правок ручек
        const itemChildren = item.children?.filter((child) => itemsById[child]?.is_published);

        const itemClassName = cn('docs-tree__list__item__text', {
            first: isRootList,
            active: item.title_for_url === docName,
            expanded: expandedNodes[itemId],
            hasChildren: itemChildren?.length,
        });

        // Клик по иконке-стрелке или по самому пункту меню
        const menuItemClickHandler = (id: string, isClickOnIcon?: boolean) => {
            // Делаем запрос если данных по потомкам нет
            const children = menuItems[id]?.children;
            const isChildrenDataExists = children.some((child) => !!menuItems[child]);
            if (children?.length && !isChildrenDataExists) getDocChildren(id);

            const nodes = { ...expandedNodes };
            nodes[id] = isClickOnIcon ? !expandedNodes[id] : true;

            onClickItem(isClickOnIcon);

            setExpandedNodes(nodes);
        };

        return (
            <div className="docs-tree__list__item">
                <Link
                    className={itemClassName}
                    to={`/${namespace}/${item.title_for_url}`}
                    onClick={() => menuItemClickHandler(itemId)}
                >
                    {/*
                        Пока отображаем стрелку по наличию детей, независимо от флага is_published, 
                        после правок на бэке возможно нужны будут корректировки
                    */}
                    {item?.children?.length ? (
                        <span
                            className="docs-tree__list__item__expand-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                menuItemClickHandler(itemId, true);
                            }}
                        >
                            <span className="docs-tree__list__item__expand-button__icon">
                                <IconShevronDown16 />
                            </span>
                        </span>
                    ) : null}
                    <span className="docs-tree__list__item__title">{item.title}</span>
                </Link>
                {itemChildren?.length ? <Menu itemIds={itemChildren} itemsById={itemsById} /> : null}
            </div>
        );
    }

    return (
        <div className="docs-tree">
            <Scrollbar horizontalScroll={false} verticalScroll>
                <Loader loading={isDocChildrenLoading}>
                    {!hideSearch && <div className='docs-tree__search'><Search /></div>}
                    <Menu itemIds={rootIds} itemsById={menuItems} isRootList />
                </Loader>
            </Scrollbar>
        </div>
    );
};

export default DocumentsTree;
